/**
 * SLA Detail Page
 * 
 * Displays detailed information for a single Channel SLA or Stage SLA.
 * Supports deep linking via URL parameters.
 */

import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Clock, 
  User, 
  Phone,
  Zap,
  AlertTriangle,
  FileText,
  Target,
  CheckCircle
} from 'lucide-react';
import { 
  getChannelSLAById, 
  getStageSLAById,
  getExceptionCodesForStage
} from '../../lib/slaCatalogueRegistry';
import { getStageById } from '../../lib/leadPipelineRegistry';
import type { ChannelSLA, StageSLA, SLAPriority } from '../../types';

// =============================================================================
// Main Component
// =============================================================================

const SLADetail: React.FC = () => {
  const { type, id } = useParams<{ type: 'channel' | 'stage'; id: string }>();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  // Validate params
  if (!type || !id || (type !== 'channel' && type !== 'stage')) {
    return <Navigate to="/sla-catalogue" replace />;
  }

  // Get SLA data
  const sla = type === 'channel' 
    ? getChannelSLAById(id) 
    : getStageSLAById(id);

  if (!sla) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'الاتفاقية غير موجودة' : 'SLA Not Found'}
        </h1>
        <Link 
          to="/sla-catalogue" 
          className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {isArabic ? 'العودة إلى الكتالوج' : 'Back to Catalogue'}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm">
        <Link 
          to="/sla-catalogue" 
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
        >
          <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
          {isArabic ? 'كتالوج الاتفاقيات' : 'SLA Catalogue'}
        </Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {type === 'channel' 
            ? (isArabic ? 'اتفاقية القناة' : 'Channel SLA')
            : (isArabic ? 'اتفاقية المرحلة' : 'Stage SLA')
          }
        </span>
      </div>

      {/* Render appropriate detail view */}
      {type === 'channel' ? (
        <ChannelSLADetail sla={sla as ChannelSLA} isArabic={isArabic} />
      ) : (
        <StageSLADetail sla={sla as StageSLA} isArabic={isArabic} />
      )}
    </div>
  );
};

// =============================================================================
// Channel SLA Detail
// =============================================================================

const ChannelSLADetail: React.FC<{ sla: ChannelSLA; isArabic: boolean }> = ({ 
  sla, 
  isArabic 
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/20">
            <Phone className="w-7 h-7" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 rounded text-xs font-medium bg-white/20">
                {isArabic ? 'اتفاقية قناة' : 'Channel SLA'}
              </span>
              <PriorityBadge priority={sla.priority} isArabic={isArabic} light />
            </div>
            
            <h1 className="text-2xl font-bold mb-2">
              {isArabic ? sla.channelAr : sla.channel}
            </h1>
            
            <div className="flex flex-wrap gap-4 text-sm mt-4">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <User className="w-4 h-4" />
                <span>{isArabic ? sla.ownerAr : sla.owner}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <Clock className="w-4 h-4" />
                <span>
                  {isArabic ? 'وقت الاستجابة: ' : 'Response: '}
                  {isArabic ? sla.timeToFirstActionAr : sla.timeToFirstAction}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'تفاصيل الاتفاقية' : 'SLA Details'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DetailCard 
            icon={<Clock className="w-5 h-5 text-blue-500" />}
            label={isArabic ? 'وقت أول إجراء' : 'Time to First Action'}
            value={isArabic ? sla.timeToFirstActionAr : sla.timeToFirstAction}
          />
          <DetailCard 
            icon={<Phone className="w-5 h-5 text-green-500" />}
            label={isArabic ? 'وقت أول اتصال' : 'Time to First Contact'}
            value={isArabic ? sla.timeToFirstContactAr : sla.timeToFirstContact}
          />
          <DetailCard 
            icon={<Target className="w-5 h-5 text-purple-500" />}
            label={isArabic ? 'عدد المحاولات المطلوبة' : 'Required Attempts'}
            value={String(sla.requiredAttempts)}
          />
        </div>
      </div>

      {/* Attempt Cadence */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          {isArabic ? 'جدول المحاولات' : 'Attempt Cadence'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
          {isArabic ? sla.attemptCadenceAr : sla.attemptCadence}
        </p>
      </div>

      {/* Notes */}
      {(sla.notes || sla.notesAr) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-6">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {isArabic ? 'ملاحظات مهمة' : 'Important Notes'}
          </h2>
          <p className="text-amber-800 dark:text-amber-200">
            {isArabic ? sla.notesAr : sla.notes}
          </p>
        </div>
      )}

      {/* Applicable Brands */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'العلامات التجارية المطبقة' : 'Applicable Brands'}
        </h2>
        <div className="flex flex-wrap gap-2">
          {sla.appliesToBrands.map((brand) => (
            <span 
              key={brand}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium"
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Stage SLA Detail
// =============================================================================

const StageSLADetail: React.FC<{ sla: StageSLA; isArabic: boolean }> = ({ 
  sla, 
  isArabic 
}) => {
  // Get linked pipeline stage info
  const pipelineStage = getStageById(sla.stageId);
  
  // Get applicable exception codes
  const exceptionCodes = getExceptionCodesForStage(sla.stageId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white/20">
            <Zap className="w-7 h-7" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 rounded text-xs font-medium bg-white/20">
                {isArabic ? 'اتفاقية مرحلة' : 'Stage SLA'}
              </span>
              {pipelineStage && (
                <Link 
                  to={`/pipeline/stage/${sla.stageId}`}
                  className="px-2 py-1 rounded text-xs font-medium bg-white/20 hover:bg-white/30 transition-colors"
                >
                  {isArabic ? 'عرض المرحلة' : 'View Stage'} →
                </Link>
              )}
            </div>
            
            <h1 className="text-2xl font-bold mb-2">
              {isArabic ? sla.stageNameAr : sla.stageName}
            </h1>
            
            <div className="flex flex-wrap gap-4 text-sm mt-4">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <User className="w-4 h-4" />
                <span>{isArabic ? sla.ownerAr : sla.owner}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <Clock className="w-4 h-4" />
                <span>
                  {isArabic ? 'الحد الأقصى: ' : 'Max: '}
                  {isArabic ? sla.maxDurationAr : sla.maxDuration}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'أهداف الوقت' : 'Time Targets'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DetailCard 
            icon={<Clock className="w-5 h-5 text-red-500" />}
            label={isArabic ? 'الحد الأقصى للمدة' : 'Max Duration'}
            value={isArabic ? sla.maxDurationAr : sla.maxDuration}
          />
          <DetailCard 
            icon={<Target className="w-5 h-5 text-green-500" />}
            label={isArabic ? 'المدة المستهدفة' : 'Target Duration'}
            value={isArabic ? sla.targetDurationAr : sla.targetDuration}
          />
          <DetailCard 
            icon={<Zap className="w-5 h-5 text-blue-500" />}
            label={isArabic ? 'وقت أول إجراء' : 'First Action SLA'}
            value={isArabic ? sla.firstActionSLAAr : sla.firstActionSLA}
          />
          <DetailCard 
            icon={<Clock className="w-5 h-5 text-purple-500" />}
            label={isArabic ? 'المتابعة' : 'Follow-up SLA'}
            value={isArabic ? sla.followUpSLAAr : sla.followUpSLA}
          />
          {sla.handoverSLA && (
            <DetailCard 
              icon={<User className="w-5 h-5 text-amber-500" />}
              label={isArabic ? 'وقت التسليم' : 'Handover SLA'}
              value={isArabic ? sla.handoverSLAAr || sla.handoverSLA : sla.handoverSLA}
            />
          )}
        </div>
      </div>

      {/* Escalation Trigger */}
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-6">
        <h2 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {isArabic ? 'محفز التصعيد' : 'Escalation Trigger'}
        </h2>
        <p className="text-red-800 dark:text-red-200">
          {isArabic ? sla.escalationTriggerAr : sla.escalationTrigger}
        </p>
      </div>

      {/* KPI References */}
      {sla.kpiReferences.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            {isArabic ? 'مؤشرات الأداء المرتبطة' : 'Related KPIs'}
          </h2>
          <div className="flex flex-wrap gap-2">
            {sla.kpiReferences.map((kpi, idx) => (
              <span 
                key={idx}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium"
              >
                {kpi}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Applicable Exception Codes */}
      {exceptionCodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            {isArabic ? 'رموز الاستثناء المتاحة' : 'Available Exception Codes'}
          </h2>
          <div className="space-y-2">
            {exceptionCodes.slice(0, 5).map((code) => (
              <div 
                key={code.code}
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <span className="font-mono text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                  {code.code}
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {isArabic ? code.labelAr : code.label}
                </span>
              </div>
            ))}
            {exceptionCodes.length > 5 && (
              <Link 
                to="/sla-catalogue"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {isArabic 
                  ? `+${exceptionCodes.length - 5} المزيد من الاستثناءات`
                  : `+${exceptionCodes.length - 5} more exceptions`
                }
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Applicable Brands */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'العلامات التجارية المطبقة' : 'Applicable Brands'}
        </h2>
        <div className="flex flex-wrap gap-2">
          {sla.appliesToBrands.map((brand) => (
            <span 
              key={brand}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium"
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Helper Components
// =============================================================================

const DetailCard: React.FC<{ 
  icon: React.ReactNode; 
  label: string; 
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
    {icon}
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 dark:text-white mt-1">{value}</dd>
    </div>
  </div>
);

const PriorityBadge: React.FC<{ 
  priority: SLAPriority; 
  isArabic: boolean;
  light?: boolean;
}> = ({ priority, isArabic, light }) => {
  const labels = {
    critical: isArabic ? 'حرج' : 'Critical',
    high: isArabic ? 'عالي' : 'High',
    medium: isArabic ? 'متوسط' : 'Medium',
    low: isArabic ? 'منخفض' : 'Low',
  };
  
  const colors = {
    critical: light ? 'bg-red-500/30 text-white' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    high: light ? 'bg-orange-500/30 text-white' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    medium: light ? 'bg-yellow-500/30 text-white' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    low: light ? 'bg-green-500/30 text-white' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  };
  
  return (
    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${colors[priority]}`}>
      {labels[priority]}
    </span>
  );
};

export default SLADetail;
